import {Component, Input, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {ModalController} from '@ionic/angular';
import {AvanzamentoService} from '../../../_services/avanzamento.service';
import {MovimentiService} from '../../../_services/movimenti.service';
import {ArticoliService} from '../../../_services/articoli.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {LicenzeService} from '../../../_services/licenze.service';
import {SaveMessage} from '../../../_helpers/save-message';
import {constants} from '../../../_helpers/constants';
import {ContenitoreInfo, FaseInfo, PosizionePercorso, RamoProssimaFase} from '../../../_models/ContenitoreInfo';
import {VistaPercorso} from '../../../_models/AccountOrganizzazione';
import {Contenitore} from '../../../_models/Contenitore';
import {ContenitoreFormato} from '../../../_models/ContenitoreFormato';
import {CardFase, instradaAzione} from '../contenitore-percorso/contenitore-percorso.model';

export interface AvanzamentoPercorso {
  passate: number;
  attuale: number;
  daFare: number;
  percPassate: number;
  percAttuale: number;
}

// Fasi del percorso: passate, attuale e da fare (prossime comprese); alternative e fuori percorso escluse.
export const avanzamentoPercorso = (info: ContenitoreInfo): AvanzamentoPercorso | null => {
  const fasi = (info.percorso ?? []).flatMap(r => r.fasi);
  const conta = (...p: PosizionePercorso[]) => fasi.filter(f => p.includes(f.posizione ?? PosizionePercorso.DaFare)).length;
  const passate = conta(PosizionePercorso.Passata);
  const attuale = conta(PosizionePercorso.Attuale);
  const daFare = conta(PosizionePercorso.Prossima, PosizionePercorso.DaFare);
  const totale = passate + attuale + daFare;
  return totale === 0 ? null : {
    passate, attuale, daFare, percPassate: passate * 100 / totale, percAttuale: attuale * 100 / totale
  };
};

/**
 * Feature 162 «Info contenitore»: contenuto, fase da cui è generato e prossima fase (regole 021).
 * Feature 166: fasi nel percorso (vista e dettaglio dalle opzioni operatore), un bottone per card.
 * Sola lettura; APRI/LANCIA compaiono solo se la schermata chiamante offre le azioni.
 */
@Component({
  selector: 'app-contenitore-info',
  templateUrl: './contenitore-info.component.html',
  styleUrls: ['./contenitore-info.component.scss'],
  standalone: false
})
export class ContenitoreInfoComponent implements OnInit {
  @Input() codBox = '';
  // Truthy solo dall'interfaccia manuale: abilita APRI/LANCIA.
  @Input() azioni: unknown = null;
  info: ContenitoreInfo | null = null;
  // Feature 166, card «Avanzamento percorso» della Timeline: calcolata una volta al caricamento.
  avanzamento: AvanzamentoPercorso | null = null;
  // Feature 166, Mappa (mockup C): fase scelta nel percorso, mostrata nel footer con APRI/LANCIA e Storia.
  faseFooter: CardFase | null = null;
  // Formato del contenitore (id → codice e descrizione) dalla cache dei formati dell'organizzazione.
  formato: ContenitoreFormato | null = null;
  // Descrizione della variante dell'articolo, se il contenitore ne ha una (stessa ricerca di app-articolo-variante-input).
  descVariante = '';
  loading = false;
  nonDisponibile = false;
  protected readonly constants = constants;
  protected readonly Ramo = RamoProssimaFase;
  protected readonly Vista = VistaPercorso;

  constructor(
    private modalCtrl: ModalController,
    private router: Router,
    private avanzamentoService: AvanzamentoService,
    private movimentiService: MovimentiService,
    private articoliService: ArticoliService,
    private organizzazioniService: OrganizzazioniService,
    public licenseService: LicenzeService,
    private saveMessage: SaveMessage) { }

  ngOnInit() {
    if (this.codBox) {
      void this.cerca();
    }
  }

  get chiudiFasiPrecedenti(): boolean {
    return this.organizzazioniService.chiudiFasiPrecedenti;
  }

  get vista(): VistaPercorso {
    return this.organizzazioniService.tipoVistaPercorso;
  }

  // Il percorso c'è solo per un contenitore di produzione.
  get infoPercorso(): ContenitoreInfo | null {
    return this.info?.contenitore && this.info.daProduzione ? this.info : null;
  }

  get storiaDisponibile(): boolean {
    return this.licenseService.checkPermesso(constants.MOD_PRO_BASE_STORIA) && !!this.info?.daProduzione;
  }

  async cerca() {
    const codice = (this.codBox ?? '').trim();
    if (!codice) {
      return;
    }
    this.loading = true;
    this.nonDisponibile = false;
    try {
      this.info = await this.avanzamentoService.getContenitoreInfo(
        this.organizzazioniService.organizzazioneId, codice, this.organizzazioniService.filtroOrdini,
        this.organizzazioniService.tipoDettaglioPercorso);
      this.avanzamento = avanzamentoPercorso(this.info);
      void this.caricaFormato(this.info.contenitore?.formato);
      void this.caricaVariante(this.info.contenitore?.codArt, this.info.contenitore?.varianteArt);
    } catch (err) {
      this.onErrore(err);
    } finally {
      this.loading = false;
    }
  }

  // Il codice scansionato in app-box-input è di un contenitore esistente: si caricano le informazioni.
  onContenitore(contenitore: Contenitore) {
    this.codBox = contenitore.codice;
    void this.cerca();
  }

  // Attuale e prossime: apribilità già calcolata, si riusa il flusso di apertura del servizio del modale.
  apri(fase: FaseInfo) {
    if (fase.puoAprire) {
      void this.modalCtrl.dismiss(fase, 'confirm');
    }
  }

  onAzioneFooter(card: CardFase) {
    instradaAzione(card, f => this.apri(f), f => this.cercaFase(f));
  }

  // Altre fasi (D2): la stessa ricerca della scansione del codice ordine, fatta da chi ha aperto il modale.
  cercaFase(fase: FaseInfo) {
    void this.modalCtrl.dismiss(fase, 'cerca');
  }

  async apriStoria(daCard?: FaseInfo) {
    const fase = daCard ?? this.info?.faseAttuale ?? this.info?.fasiRiga?.[0];
    if (!fase) {
      return;
    }
    await this.modalCtrl.dismiss(null, 'cancel');
    void this.router.navigate(['/ordini/storia', fase.idDocumento, fase.idRiga]);
  }

  cancel = () => this.modalCtrl.dismiss(null, 'cancel');

  // Il formato non blocca il resto: se non si legge si mostra «-».
  private async caricaFormato(formatoId?: number) {
    this.formato = null;
    try {
      this.formato = await this.movimentiService.getContenitoreFormatoCachedAsync(
        this.organizzazioniService.organizzazioneId, formatoId ?? 0);
    } catch {
      this.formato = null;
    }
  }

  // Come il formato: se la descrizione non si legge resta solo il codice della variante.
  private async caricaVariante(codArt?: string, variante?: string) {
    this.descVariante = '';
    if (!codArt || !variante) {
      return;
    }
    try {
      const varianti = await this.articoliService.searchVariantiOfArticolo(this.organizzazioniService.organizzazioneId, codArt);
      this.descVariante = varianti?.result?.find(v => v.varianteArt === variante)?.descrizione ?? '';
    } catch {
      this.descVariante = '';
    }
  }

  // 404: LocalApi senza l'endpoint (aggiornare LocalApi insieme al client).
  private onErrore(err: unknown) {
    this.info = null;
    this.avanzamento = null;
    this.faseFooter = null;
    this.formato = null;
    this.descVariante = '';
    if ((err as HttpErrorResponse | undefined)?.status === 404) {
      this.nonDisponibile = true;
      return;
    }
    this.saveMessage.messageError('Ci sono problemi con il caricamento delle informazioni del contenitore.');
  }
}
